import { NavLink, useParams } from 'react-router-dom';
import { isCityId } from '../lib/cities';
import './TabBar.css';
import { useT } from '../i18n';

/**
 * Primary navigation.
 *
 * v2 used bare emoji as the only label on mobile, which renders differently on
 * every platform and gives assistive tech nothing to read. These are inline SVG
 * with a permanent text label — the tab bar is the app's spine, and hiding what
 * the destinations are to save 10px is a bad trade.
 *
 * The city is carried through the URL so switching tabs and coming back does not
 * dump you in a different city.
 */
const TABS = [
    {
        to: (city: string) => `/city/${city}`,
        labelKey: 'nav.find' as const,
        match: /^\/city\/[^/]+(\/place\/.*)?$/,
        path: 'M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11Z M12 10.5a1.5 1.5 0 1 0 0-.01',
    },
    {
        to: (city: string) => `/city/${city}/prayer`,
        labelKey: 'nav.prayer' as const,
        match: /\/(prayer|qibla)$/,
        path: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z M15.5 8.5 10 10l-1.5 5.5L14 14l1.5-5.5Z',
    },
    {
        to: (city: string) => `/city/${city}/cards`,
        labelKey: 'nav.cards' as const,
        match: /\/cards$/,
        path: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z M7 10h6M7 14h4',
    },
    {
        to: () => '/saved',
        labelKey: 'nav.saved' as const,
        match: /^\/saved$/,
        path: 'M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16Z',
    },
];

export function TabBar() {
    const { t } = useT();
    const { cityId } = useParams();
    const city = isCityId(cityId) ? cityId : 'hongkong';

    return (
        <nav className="tabbar" aria-label={t('nav.main')}>
            {TABS.map((tab) => (
                <NavLink
                    key={tab.labelKey}
                    to={tab.to(city)}
                    className={({ isActive }) => `tabbar__tab ${isActive || tab.match.test(location.pathname) ? 'is-active' : ''}`}
                    end={tab.labelKey === 'nav.find'}
                >
                    <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor"
                         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d={tab.path} />
                    </svg>
                    <span>{t(tab.labelKey)}</span>
                </NavLink>
            ))}
        </nav>
    );
}
